/**
 * Shared prompt utilities for operation detail pages.
 *
 * Extracted from SubmissionDetail + HintDetail to avoid duplication.
 */

import { useMemo } from 'react';

export const formatCost = (cost?: number) =>
    cost === undefined ? '-' : `$${cost.toFixed(4)}`;

export const formatLatency = (ms?: number) =>
    ms === undefined ? '-' : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;

export interface PromptInfo {
    id: string;
    name: string;
    type: string;
}

export interface PromptLookup {
    getPromptName: (promptId: string) => string;
    getPromptType: (promptId: string) => string | undefined;
}

/**
 * Hook that builds name/type lookup maps from a prompt list.
 * Handles versioned prompt IDs (e.g. `base.3` → looks up `base`).
 */
export function usePromptLookup(prompts: PromptInfo[] | undefined): PromptLookup {
    const maps = useMemo(() => {
        const nameMap = new Map<string, string>();
        const typeMap = new Map<string, string>();
        prompts?.forEach(p => {
            nameMap.set(p.id, p.name);
            typeMap.set(p.id, p.type);
        });
        return { nameMap, typeMap };
    }, [prompts]);

    const getPromptName = (promptId: string): string => {
        if (maps.nameMap.has(promptId)) return maps.nameMap.get(promptId)!;
        const dot = promptId.lastIndexOf('.');
        if (dot > 0) {
            const baseId = promptId.substring(0, dot);
            if (maps.nameMap.has(baseId)) return maps.nameMap.get(baseId)!;
        }
        return promptId;
    };

    const getPromptType = (promptId: string): string | undefined => {
        if (maps.typeMap.has(promptId)) return maps.typeMap.get(promptId);
        const dot = promptId.lastIndexOf('.');
        if (dot > 0) {
            const baseId = promptId.substring(0, dot);
            return maps.typeMap.get(baseId);
        }
        return undefined;
    };

    return { getPromptName, getPromptType };
}

/**
 * What every model call of one operation cost, summed — the one cost figure an
 * operation page shows. Rows are the operation's model outputs (every call it
 * made, failed failover hops and escalations included, since each was billed);
 * a row without a cost (a call still running, or one before cost was recorded)
 * adds nothing. Undefined when no row carries a cost at all.
 */
export function totalRunCost(rows: ReadonlyArray<{ cost?: number | null }>): number | undefined {
    let total: number | undefined;
    for (const r of rows) {
        if (typeof r.cost === 'number' && Number.isFinite(r.cost)) total = (total ?? 0) + r.cost;
    }
    return total;
}
